import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AeroFlightSummaryCard from "../../components/aero/AeroFlightSummaryCard";
import AeroInfoModal from "../../components/aero/AeroInfoModal";
import { AeroCheckoutState } from "../../types/darwin";
import {
    holdFlightInventory,
    HoldFlightInventoryResponse,
    searchDarwinFlights,
} from "../../services/darwinApi";
import { MovmoCheckoutButton, consumeMovmoCheckoutReturn } from "@movmo/express-checkout-button";
import "@movmo/express-checkout-button/dist/index.css";
import { useMockMode } from "../../hooks/useMockMode";
import { buildMovmoFlightOfferForAero } from "../../services/mockOffers";
import { useLinkedTravelerState } from "../../hooks/linkedTravelerStore";
import { queryWithoutMovmo } from "../../utils/preserveQuery";

/* Small Aero gradient logo — matches AeroFlightResults */
const AeroLogoSmall: React.FC = () => (
    <div
        className="flex flex-col overflow-hidden"
        style={{ width: 32, height: 42, borderRadius: 14 }}
    >
        {["#a40000", "#c51600", "#e42c01", "#fb6900", "#fc9301", "#fdc002"].map(
            (c) => (
                <div
                    key={c}
                    className="flex-1"
                    style={{ backgroundColor: c }}
                />
            ),
        )}
    </div>
);

const BackArrow: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="currentColor">
        <path d="M38 22.5H14.83l10.59-10.58L24 10.5l-14 14 14 14 1.41-1.41L14.83 26.5H38v-4Z" />
    </svg>
);

const PersonIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 48 48" fill="currentColor">
        <path d="M24 24c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8Zm0 4c5.33 0 16 2.67 16 8v4H8v-4c0-5.33 10.67-8 16-8Z" />
    </svg>
);

const AeroCheckout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as AeroCheckoutState | null;
    const mockMode = useMockMode();
    // Native personalization (MOVMO-508 PR7): AeroCheckout has no separate
    // guest/contact form of its own — Movmo Express Checkout collects
    // passenger info inside its own iframe. So "pre-fill" here means passing
    // the linked profile (read from the Aero session at landing) through as the affordance shown below; the
    // actual field population happens inside the Movmo checkout iframe.
    const { traveler } = useLinkedTravelerState("aero");

    const [showModal, setShowModal] = useState(true);
    const [holdSessionId, setHoldSessionId] = useState<string | undefined>();
    const [holdDetails, setHoldDetails] =
        useState<HoldFlightInventoryResponse | null>(null);
    const [holdError, setHoldError] = useState<string | null>(null);
    const [bookingConfirmed, setBookingConfirmed] = useState(false);
    const [movmoBookingId, setMovmoBookingId] = useState<string | undefined>();
    const holdCalledRef = useRef(false);

    const departure = state?.departure;
    const returnFlight = state?.returnFlight;
    const adults = state?.adults ?? 1;
    const infants = state?.infants ?? 0;
    const tripType = state?.tripType;
    const sessionId = state?.sessionId;

    // MOVMO-549: Handle page-level return from top-level redirect.
    // When the browser does a fresh load of the checkout URL after
    // a redirect from Movmo, location.state is empty (not routed via
    // React navigate). Consume the return params here so the button
    // can render and show the confirmation banner on return.
    useEffect(() => {
        consumeMovmoCheckoutReturn({
            isProd: false,
            onBookingComplete: (info: any) => {
                console.log("[aero-checkout] booking complete:", info);
                setMovmoBookingId(info.bookingId);
            },
            onExit: ({ reason }: { reason: string }) => {
                console.log("[aero-checkout] movmo checkout exit:", reason);
                if (reason === "booking_confirmed") {
                    setBookingConfirmed(true);
                } else if (reason !== undefined) {
                    navigate("/aero/flights" + queryWithoutMovmo(window.location.search));
                }
            },
        });
    }, []);

    // On mount: call performAvailability (with both legs for round-trip) to get a
    // single session, then holdFlightInventory with that session. This mirrors what
    // Aero's real website does on their checkout page — a combined search + hold
    // ensures all legs share the same Darwin session.
    useEffect(() => {
        if (!departure || holdCalledRef.current) return;
        holdCalledRef.current = true;

        // Mock mode skips the Darwin session/hold dance entirely. The
        // MovmoCheckoutButton handoff below uses a synthetic
        // MovmoFlightOffer instead of the darwinHeldFlight payload, so
        // we don't need a real session ID.
        if (mockMode) {
            return;
        }

        const run = async () => {
            try {
                // Build availability request with all legs in one call
                const depTf = departure.trip.tripFlight;
                let depDate = depTf.departureDate;
                if (depDate.includes("T")) depDate = depDate.split("T")[0];

                const availDetails: {
                    origin: string;
                    destination: string;
                    departureDateStart: string;
                    departureDateEnd: string;
                    flightNumber?: string;
                }[] = [
                    {
                        origin: depTf.origin,
                        destination: depTf.destination,
                        departureDateStart: depDate,
                        departureDateEnd: depDate,
                        flightNumber: depTf.flightNumber,
                    },
                ];

                if (returnFlight) {
                    const retTf = returnFlight.trip.tripFlight;
                    let retDate = retTf.departureDate;
                    if (retDate.includes("T")) retDate = retDate.split("T")[0];
                    availDetails.push({
                        origin: retTf.origin,
                        destination: retTf.destination,
                        departureDateStart: retDate,
                        departureDateEnd: retDate,
                        flightNumber: retTf.flightNumber,
                    });
                }

                // Step 1: Combined performAvailability to get single session
                const availResp = await searchDarwinFlights({
                    seatsRequested: adults + infants,
                    showSoldoutFlights: false,
                    currencyCode: "USD",
                    availabilityReqDetails: availDetails,
                });

                const combinedSessionId = availResp.sessionId;
                if (!combinedSessionId) {
                    console.error(
                        "No sessionId from combined performAvailability",
                    );
                    setHoldError("Failed to establish session.");
                    return;
                }

                // Step 2: Hold all flights under the combined session
                const flights = returnFlight
                    ? [departure, returnFlight]
                    : [departure];
                const flightsToHold = flights.map((sf) => {
                    const tf = sf.trip.tripFlight;
                    let d = tf.departureDate;
                    if (d.includes("T")) d = d.split("T")[0];
                    return {
                        marketingCarrierCode: tf.marketingCarrierCode,
                        origin: tf.origin,
                        destination: tf.destination,
                        flightNumber: tf.flightNumber,
                        departureDate: d,
                        departureTime: tf.departureTime,
                        arrivalTime: tf.arrivalTime,
                        fareSummaryUid:
                            sf.fareClass?.fares?.[0]?.fareSummaryUid || "",
                    };
                });

                const ptcHoldCounts: { ptc: string; holdCount: number }[] = [];
                if (adults > 0)
                    ptcHoldCounts.push({ ptc: "ADT", holdCount: adults });
                if (infants > 0)
                    ptcHoldCounts.push({ ptc: "INF", holdCount: infants });
                if (ptcHoldCounts.length === 0)
                    ptcHoldCounts.push({ ptc: "ADT", holdCount: 1 });

                const holdResp = await holdFlightInventory({
                    sessionId: combinedSessionId,
                    flightsToHold,
                    ptcHoldCounts,
                    currencyCode: "USD",
                });

                setHoldDetails(holdResp);
                setHoldSessionId(holdResp.sessionId);
            } catch (err) {
                console.error("Checkout session setup failed:", err);
                setHoldError("Failed to reserve flights. Please try again.");
            }
        };

        run();
    }, [departure, returnFlight, adults, infants]);

    // Guard: redirect if no state, UNLESS we're showing a booking confirmation
    // (which sets bookingConfirmed via the page-level return consumer above).
    if (!state || !departure) {
        if (!bookingConfirmed) {
            navigate("/aero");
            return null;
        }
        // Fall through: bookingConfirmed is true, render minimal confirmation page
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fff", padding: 24 }}>
                <div style={{ maxWidth: 480, textAlign: "center" }}>
                    {bookingConfirmed && (
                        <div
                            style={{
                                padding: "24px",
                                backgroundColor: "#f0fdf4",
                                border: "1px solid #86efac",
                                borderRadius: 8,
                                marginBottom: 24,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 18,
                                    fontWeight: 600,
                                    color: "#22c55e",
                                    marginBottom: 8,
                                }}
                            >
                                Booking confirmed with Movmo
                            </div>
                            {movmoBookingId && (
                                <div
                                    style={{
                                        fontSize: 14,
                                        color: "#16a34a",
                                        marginBottom: 16,
                                    }}
                                >
                                    Movmo booking ID: {movmoBookingId}
                                </div>
                            )}
                            <button
                                onClick={() => navigate("/aero/flights")}
                                style={{
                                    padding: "12px 24px",
                                    backgroundColor: "#22c55e",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    fontSize: 14,
                                    fontWeight: 500,
                                }}
                            >
                                Return to Search
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Price calculation — matches aero.com's checkout structure:
    // "Adults (N)" = per-person base fare (both legs combined)
    // "Taxes and fees" = total taxes across all legs × adults
    // "Subtotal" = grand total
    // "Total" = grand total
    const perPersonBaseFare =
        departure.ptcFare.baseFare + (returnFlight?.ptcFare.baseFare ?? 0);
    const departureTaxes = departure.ptcFare.ptcTaxes.reduce(
        (sum, t) => sum + t.amount,
        0,
    );
    const returnTaxes = returnFlight
        ? returnFlight.ptcFare.ptcTaxes.reduce((sum, t) => sum + t.amount, 0)
        : 0;
    const totalTaxes = (departureTaxes + returnTaxes) * adults;
    const grandTotal =
        (departure.ptcFare.totalFare + (returnFlight?.ptcFare.totalFare ?? 0)) *
        adults;

    const formatPrice = (n: number) =>
        n.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const origin = departure.trip.tripFlight.origin;
    const destination = departure.trip.tripFlight.destination;

    return (
        <div
            className="flex h-screen overflow-hidden"
            style={{
                fontFamily:
                    '"Haas Grot Disp Web", "Haas Grot Text Web", "Helvetica Neue", Helvetica, Arial, sans-serif',
            }}
        >
            {/* Pre-boarding screening modal — single modal on page load */}
            <AeroInfoModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
            />

            {/* ── Black logo column — 80px fixed, full height ── */}
            <div
                className="flex-shrink-0"
                style={{
                    width: 80,
                    backgroundColor: "#000",
                    padding: 24,
                }}
            >
                <a href="/aero">
                    <AeroLogoSmall />
                </a>
            </div>

            {/* ── Gray cards panel — scrollable, holds flight summary cards ── */}
            <div
                className="flex-shrink-0"
                style={{
                    width: "calc(43% - 80px)",
                    backgroundColor: "#f3f3f3",
                    overflowY: "auto",
                    padding: 24,
                }}
            >
                <div style={{ padding: 32 }}>
                    <div
                        style={{
                            maxWidth: 480,
                            margin: "0 auto",
                        }}
                    >
                        <AeroFlightSummaryCard
                            label="Departure"
                            selectedFlight={departure}
                        />
                        {returnFlight && (
                            <div style={{ marginTop: 56 }}>
                                <AeroFlightSummaryCard
                                    label="Return"
                                    selectedFlight={returnFlight}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Right panel (~60%) — white background, trip summary ── */}
            <div
                className="flex-1 overflow-y-auto"
                style={{ padding: 24, backgroundColor: "#fff" }}
            >
                {/* Content constrained to ~480px */}
                <div
                    style={{ maxWidth: 480, margin: "0 auto", paddingTop: 32 }}
                >
                    {/* Top bar: back arrow + route summary */}
                    <div className="flex items-center">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center hover:opacity-70 transition-opacity"
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                color: "#000",
                            }}
                        >
                            <BackArrow />
                        </button>

                        {/* Route summary — right aligned */}
                        <div
                            className="flex items-center gap-2 ml-auto"
                            style={{ fontSize: 14, color: "#949494" }}
                        >
                            <span>{origin}</span>
                            <span>→</span>
                            <span>{destination}</span>
                            <span style={{ margin: "0 4px" }}>|</span>
                            <span>
                                {tripType === "roundtrip"
                                    ? "Round Trip"
                                    : "One Way"}
                            </span>
                            <span style={{ margin: "0 4px" }}>|</span>
                            <span className="flex items-center gap-1">
                                <PersonIcon />
                                {adults}
                            </span>
                            {infants > 0 && (
                                <span className="flex items-center gap-1">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 48 48"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M25.6 15.67c-3.4 1.34-6.6-1.65-6.6-5.18h4c0 1.48 1.04 1.5 1.15 1.46.09-.04.54-.27.81-1.42.19-.8.26-1.94.07-3.51L24 7c-8.42 0-12.8 3.76-14.98 7.9a17.4 17.4 0 0 0-1.74 5.4 5 5 0 0 0 .2 9.46c.3 1.4.78 2.89 1.54 4.33C11.2 38.24 15.58 42 24 42c8.42 0 12.8-3.76 14.98-7.9a17.06 17.06 0 0 0 1.55-4.34 5 5 0 0 0 .2-9.45 17.41 17.41 0 0 0-1.75-5.4c-1.69-3.22-4.7-6.2-9.88-7.38.1 1.5.02 2.81-.25 3.92-.47 2.02-1.59 3.57-3.24 4.22ZM15.18 29.1c.15.83.42 1.73.87 2.6C17.2 33.97 19.52 36 24 36s6.8-2.04 7.96-4.3c.45-.87.72-1.77.87-2.6H15.17ZM20 22.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM30.5 25a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    {infants}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Trip Summary heading */}
                    <h1
                        style={{
                            fontSize: 32,
                            fontWeight: 400,
                            lineHeight: "40px",
                            color: "#000",
                            margin: "32px 0 8px 0",
                        }}
                    >
                        Trip Summary
                    </h1>

                    {/* Order details sub-heading */}
                    <div
                        style={{
                            fontSize: 24,
                            fontWeight: 400,
                            lineHeight: "28px",
                            color: "#000",
                            margin: "0 0 16px 0",
                        }}
                    >
                        Order details
                    </div>

                    {/* Price line items — 14px, weight 500 matching aero.com */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        {/* Adults */}
                        <div className="flex items-center justify-between">
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#949494",
                                }}
                            >
                                Adults ({adults})
                            </span>
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#000",
                                }}
                            >
                                {adults}x ${formatPrice(perPersonBaseFare)}
                            </span>
                        </div>

                        {/* Infants — shown only when > 0, always "Free" */}
                        {infants > 0 && (
                            <div className="flex items-center justify-between">
                                <span
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#949494",
                                    }}
                                >
                                    Infants ({infants})
                                </span>
                                <span
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#000",
                                    }}
                                >
                                    Free
                                </span>
                            </div>
                        )}

                        {/* Taxes and fees */}
                        <div className="flex items-center justify-between">
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#949494",
                                }}
                            >
                                Taxes and fees
                            </span>
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#000",
                                }}
                            >
                                ${formatPrice(totalTaxes)}
                            </span>
                        </div>

                        {/* Subtotal */}
                        <div className="flex items-center justify-between">
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#949494",
                                }}
                            >
                                Subtotal
                            </span>
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "#000",
                                }}
                            >
                                ${formatPrice(grandTotal)}
                            </span>
                        </div>
                    </div>

                    {/* Divider above Total */}
                    <div
                        style={{
                            borderTop: "1px solid #e9e9e9",
                            margin: "16px 0",
                        }}
                    />

                    {/* Total — 24px, weight 400 matching aero.com */}
                    <div className="flex items-center justify-between">
                        <span
                            style={{
                                fontSize: 24,
                                fontWeight: 400,
                                color: "#000",
                            }}
                        >
                            Total
                        </span>
                        <span
                            style={{
                                fontSize: 24,
                                fontWeight: 400,
                                color: "#000",
                            }}
                        >
                            ${formatPrice(grandTotal)}
                        </span>
                    </div>

                    {/* Divider below Total */}
                    <div
                        style={{
                            borderTop: "1px solid #e9e9e9",
                            margin: "16px 0 0 0",
                        }}
                    />

                    {/* Movmo-linked traveler affordance (MOVMO-508) — no
                        separate guest form exists on this page to prefill;
                        Movmo Express Checkout collects passenger info inside
                        its own iframe, so this surfaces what will carry
                        through instead of literally populating fields. */}
                    {traveler?.profile && (
                        <div
                            style={{
                                marginTop: 24,
                                padding: "12px 16px",
                                border: "1px solid #e9e9e9",
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#fff",
                                    backgroundColor: "rgb(255, 106, 0)",
                                    borderRadius: 999,
                                    padding: "2px 8px",
                                    flexShrink: 0,
                                }}
                            >
                                from Movmo
                            </span>
                            <span
                                style={{ fontSize: 13, color: "#3f3f3f" }}
                            >
                                Linked with Movmo as{" "}
                                <strong>
                                    {[
                                        traveler.profile.first_name,
                                        traveler.profile.last_name,
                                    ]
                                        .filter(Boolean)
                                        .join(" ") || traveler.profile.email}
                                </strong>
                                . Your saved details will carry through to
                                Movmo Express Checkout.
                            </span>
                        </div>
                    )}

                    {/* Continue to Guest Info button — placeholder for MOVMO-297 */}
                    <button
                        style={{
                            display: "inline-block",
                            width: "100%",
                            marginTop: 32,
                            padding: "20px 16px",
                            backgroundColor: "#000",
                            color: "#fff",
                            fontSize: 16,
                            fontWeight: 500,
                            borderRadius: 8,
                            border: "2px solid #000",
                            cursor: "pointer",
                            textAlign: "center",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#222";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#000";
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 24px",
                                alignItems: "center",
                                textAlign: "left",
                            }}
                        >
                            <span>Continue to Guest Info</span>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 48 48"
                                fill="currentColor"
                                style={{ strokeWidth: 2 }}
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M41 24 28 40h-4.5l11.28-14.58H9v-2.84h25.78L23.5 8H28L41 24Z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    </button>

                    {/* MOVMO-549: Booking confirmed banner — dismissible */}
                    {bookingConfirmed && (
                        <div
                            style={{
                                marginTop: 24,
                                padding: "16px",
                                backgroundColor: "#f0fdf4",
                                border: "1px solid #86efac",
                                borderRadius: 8,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "#22c55e",
                                        marginBottom: 4,
                                    }}
                                >
                                    Booking confirmed with Movmo
                                </div>
                                {movmoBookingId && (
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: "#16a34a",
                                        }}
                                    >
                                        Movmo booking ID: {movmoBookingId}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setBookingConfirmed(false)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#16a34a",
                                    cursor: "pointer",
                                    fontSize: 20,
                                    padding: 0,
                                    lineHeight: 1,
                                }}
                                aria-label="Dismiss booking confirmation"
                            >
                                ×
                            </button>
                        </div>
                    )}

                    {/* Movmo Express Checkout */}
                    <div style={{ marginTop: 10 }}>
                        <MovmoCheckoutButton
                            iataCode={
                                // TODO: confirm this with Aero team
                                process.env.REACT_APP_AERO_IATA_CODE || "aero"
                            }
                            apiKey={process.env.REACT_APP_MOVMO_API_KEY || ""}
                            providerID={mockMode ? "mock-aero" : "darwin"}
                            flightOffer={
                                mockMode
                                    ? buildMovmoFlightOfferForAero(
                                          departure,
                                          returnFlight ?? null,
                                          { adults, infants },
                                      )
                                    : holdDetails
                                      ? {
                                            darwinHeldFlight: {
                                                currencyCode:
                                                    holdDetails.currencyCode ||
                                                    "USD",
                                                ptcHoldCounts:
                                                    holdDetails.ptcHoldCounts ||
                                                    [],
                                                flightInventoryHeldDetails:
                                                    holdDetails.flightInventoryHeldDetails,
                                            },
                                            sessionId:
                                                holdSessionId || sessionId,
                                            fares: (returnFlight
                                                ? [departure, returnFlight]
                                                : [departure]
                                            ).map((sf) => ({
                                                fareClassCode:
                                                    sf.fareClass
                                                        ?.fareClassCode || "",
                                                cabinCode:
                                                    sf.fareClass?.cabinCode ||
                                                    "",
                                                fareBrandUid:
                                                    sf.fare?.fareBrandUid || "",
                                                fareBasisCode:
                                                    sf.fare?.fareBasisCode ||
                                                    "",
                                                fareBrandName: "Aero Select",
                                                ptcFares:
                                                    sf.fare?.ptcFares || [],
                                            })),
                                            adults,
                                            children: 0,
                                            infants,
                                        }
                                      : undefined
                            }
                            className="!w-full !text-base !font-medium !rounded-lg !py-5 !px-4"
                            onBookingComplete={(info: any) => {
                                // MOVMO-549: SDK 2.0 fires onBookingComplete
                                // when returning with a confirmed booking.
                                // Store the booking id for display in the banner.
                                console.log("[aero] booking complete:", info);
                                setMovmoBookingId(info.bookingId);
                            }}
                            onExit={({ reason }) => {
                                // SDK 2.0: fires after page loads post-redirect.
                                // MOVMO-549: 'booking_confirmed' means the
                                // traveler booked and returned via the button's
                                // default return URL — stay on the restored
                                // checkout page and show the confirmation banner.
                                // Other failure reasons (offer unavailable /
                                // non-retryable) route to search.
                                console.log("[aero] movmo checkout exit:", reason);
                                if (reason === "booking_confirmed") {
                                    setBookingConfirmed(true);
                                } else {
                                    navigate("/aero/flights" + queryWithoutMovmo(window.location.search));
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AeroCheckout;
